import { useState, useEffect } from 'react';
import { Loading } from './Loading';
import type { Project } from '@services/projectService';

interface LinkProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLink: (analysisProjectId: string) => Promise<void>;
  availableProjects: Project[];
  modernizationProjectName: string;
}

/**
 * Modal for linking a modernization project to an analysis project
 */
export function LinkProjectModal({
  isOpen,
  onClose,
  onLink,
  availableProjects,
  modernizationProjectName,
}: LinkProjectModalProps) {
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [isLinking, setIsLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedProjectId('');
      setError(null);
    }
  }, [isOpen]);

  const handleLink = async () => {
    if (!selectedProjectId) {
      setError('Please select an analysis project');
      return;
    }

    setIsLinking(true);
    setError(null);

    try {
      await onLink(selectedProjectId);
      onClose();
    } catch (err: any) {
      console.error('Failed to link project:', err);
      setError(err.message || 'Failed to link project');
    } finally {
      setIsLinking(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                Link to Analysis Project
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Link "{modernizationProjectName}" to a source analysis project
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {availableProjects.length === 0 ? (
              <div className="text-center py-8">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No analysis projects found
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Create an analysis project first, then link it to this modernization project.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 mb-4">
                  Select an analysis project to link:
                </p>

                {availableProjects.map((project) => {
                  const createdDate = new Date(project.created_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  });

                  return (
                    <label
                      key={project.id}
                      className={`
                        block border rounded-lg p-4 cursor-pointer transition-all
                        ${
                          selectedProjectId === project.id
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200 hover:border-red-300 hover:bg-gray-50'
                        }
                      `}
                    >
                      <div className="flex items-start">
                        <input
                          type="radio"
                          name="analysis-project"
                          value={project.id}
                          checked={selectedProjectId === project.id}
                          onChange={(e) => setSelectedProjectId(e.target.value)}
                          className="mt-1 h-4 w-4 text-red-600 border-gray-300 focus:ring-red-500"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900">
                              📦 {project.name}
                            </span>
                            <span
                              className={`
                                text-xs px-2 py-0.5 rounded-full
                                ${
                                  project.analysis_status === 'completed' || project.analysis_status === 'analyzed'
                                    ? 'bg-green-100 text-green-800'
                                    : project.analysis_status === 'analyzing'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-gray-100 text-gray-800'
                                }
                              `}
                            >
                              {project.analysis_status}
                            </span>
                          </div>
                          {project.description && (
                            <p className="text-sm text-gray-600 mb-1">
                              {project.description}
                            </p>
                          )}
                          <p className="text-xs text-gray-500">Created {createdDate}</p>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              disabled={isLinking}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleLink}
              disabled={isLinking || !selectedProjectId || availableProjects.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center"
            >
              {isLinking ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Linking...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                  Link Projects
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
